import React, { useEffect, useState } from "react";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Row,
  Icon,
  Block,
} from "../../components/Component";
import { Card, Spinner } from "reactstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getBrandingSubmissionDetail } from "../../slice/branding-submission-detail";
import { deleteSuccess as deleteBrandingSubmissionSuccess } from "../../slice/branding-submissions";
import { deleteBrandingSubmission } from "../../api/branding-submission";
import moment from "moment";
import { toast } from "react-toastify";
import DeleteConfirmationModal from "../admin/modals/DeleteConfimationModal";

const Field = ({ label, children }) => (
  <div className="py-2 border-bottom">
    <div className="text-muted small mb-1">{label}</div>
    <div className="fw-bold text-break" style={{ whiteSpace: "pre-wrap" }}>
      {children}
    </div>
  </div>
);

const BrandingSubmissionDetailPage = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    dispatch(getBrandingSubmissionDetail(id));
  }, [id, dispatch]);

  const { submission, loading } = useSelector(
    (state) => state.brandingSubmissionDetail
  );

  const onDelete = async () => {
    if (!id) return;
    try {
      await deleteBrandingSubmission(id);
      dispatch(deleteBrandingSubmissionSuccess(id));
      toast.success("Branding submission deleted");
      navigate("/branding-submissions");
    } catch (error) {
      toast.error(error?.message ?? "Failed to delete submission");
    }
  };

  return (
    <React.Fragment>
      <Head title="Branding Submission Detail"></Head>
      <Content>
        <BlockHead size="sm">
          <BlockBetween className="g-3">
            <BlockHeadContent>
              <BlockTitle>Branding Submission</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="d-flex" style={{ gap: 8 }}>
                <Link to={`/branding-submissions`}>
                  <Button color="light" outline className="bg-white">
                    <Icon name="arrow-left"></Icon>
                    <span>Back to list</span>
                  </Button>
                </Link>
                {submission && (
                  <Button
                    color="danger"
                    outline
                    onClick={() => setConfirmingDelete(true)}
                  >
                    <Icon name="trash"></Icon>
                    <span>Delete</span>
                  </Button>
                )}
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          {loading && !submission ? (
            <div className="text-center py-5">
              <Spinner />
            </div>
          ) : !submission ? (
            <div className="text-center py-5 text-muted">
              Submission not found
            </div>
          ) : (
            <Row className="g-3">
              <Col lg="4">
                <Card className="p-4">
                  <h6 className="title mb-3">Submitter</h6>
                  {submission.createdBy?.avatar && (
                    <div className="d-flex justify-content-center mb-3">
                      <img
                        src={submission.createdBy.avatar}
                        alt="avatar"
                        width={96}
                        height={96}
                        style={{
                          borderRadius: "50%",
                          objectFit: "cover",
                          border: "1px solid #eee",
                        }}
                      />
                    </div>
                  )}
                  <Field label="Name">
                    {submission.createdBy?.fullName ?? "Guest"}
                  </Field>
                  {submission.createdBy?.businessName && (
                    <Field label="Business name">
                      {submission.createdBy.businessName}
                    </Field>
                  )}
                  <Field label="Email">
                    {submission.createdBy?.email ?? "N/A"}
                  </Field>
                  <Field label="Role">
                    {submission.createdBy?.role ?? "N/A"}
                  </Field>
                  <Field label="Submitted at">
                    {submission.createdAt
                      ? moment(submission.createdAt).format(
                          "DD/MM/YYYY HH:mm"
                        )
                      : "N/A"}
                  </Field>
                </Card>
              </Col>
              <Col lg="8">
                <Card className="p-4">
                  <h6 className="title mb-3">Form responses</h6>
                  <Field label="Description about your business">
                    {submission.businessDesc || "—"}
                  </Field>
                  <Field label="Description about your business/brand">
                    {submission.brandDesc || "—"}
                  </Field>
                  <Field label="Require editorial / content management services">
                    {submission.needsEditorial ? "Yes" : "No"}
                  </Field>
                  {submission.needsEditorial && (
                    <Field label="Editorial / content management details">
                      {submission.editorialDesc || "—"}
                    </Field>
                  )}
                  <Field label="What are you looking for">
                    {submission.lookingFor || "—"}
                  </Field>
                  <Field label="Design requirements">
                    {submission.designRequirements || "—"}
                  </Field>
                  <Field label="Budget">{submission.budget || "—"}</Field>
                  <Field label="Deadline">
                    {submission.deadline
                      ? moment(submission.deadline).format("DD/MM/YYYY")
                      : "—"}
                  </Field>
                </Card>
              </Col>
            </Row>
          )}
        </Block>
      </Content>
      <DeleteConfirmationModal
        isOpen={confirmingDelete}
        onCloseModal={() => setConfirmingDelete(false)}
        onConfirm={onDelete}
        title="Delete branding submission?"
        desc="The submission and all its data will be permanently deleted."
      />
    </React.Fragment>
  );
};

export default BrandingSubmissionDetailPage;
